import { Box, Button, Group, Paper, Text } from '@mantine/core'
// import { IconPhotoScan } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import { CollapsibleContentSection } from '@/components/CollapsibleContentSection'
// import { EmptyState } from '@/components/EmptyState'
import { TableContentGrid } from '@/components/TableContentGrid'
import { theme } from '@/configs/theme'
import { useGlobalState } from '@/hooks/useGlobalState'
import { UploadedFilePreview } from '@/screens/ContentModuleFormScreen/UploadedFilePreview'
import { useStepResources } from '@/screens/ContentModuleFormScreen/useStepResources'
import type { GetStepsResponse, ResourceType } from '@/services/api/api.types'

import { useContentModuleScreenData } from './ContentModuleScreen'
import { FAQSectionTable } from './ModuleFAQSection'
import { ModuleResourcesTable } from './ModuleResourcesSection'

type StepSectionProps = {
  step: GetStepsResponse[0]
  index: number
  fileId?: string
}

export function StepSection(props: StepSectionProps) {
  const data = useContentModuleScreenData()

  const { api } = useGlobalState()

  const stepContentQuery = useQuery({
    queryKey: ['step', 'content', props.step.stepId, props.fileId],
    queryFn: () => api.getContentForPreview({ step_id: props.step.stepId, file_id: props.fileId }),
    enabled: !!props.step.stepId,
  })

  const stepResourcesQuery = useQuery({
    queryKey: ['step', 'resources', props.step.stepId],
    queryFn: () => api.getResources({ step_id: props.step.stepId }),
    enabled: !!props.step.stepId,
  })

  const { uploadedFileResource, sourceFileResource } = useStepResources(props.step.stepId)

  const contentAccessLabel = useMemo(() => {
    if (!sourceFileResource) return null
    const showInApp = sourceFileResource.showInApp ?? false
    const useInRag = props.step.useInRag ?? true
    if (showInApp && useInRag) return 'Full Access'
    if (showInApp && !useInRag) return 'Media Only'
    return 'Information Only'
  }, [sourceFileResource, props.step.useInRag])

  const rows = useMemo(() => {
    const result = [
      { heading: 'File Title', content: <Text fw={600}>{props.step.name}</Text> },
      {
        heading: 'Source',
        content: uploadedFileResource ? (
          <UploadedFilePreview
            fileName={uploadedFileResource.fileName || 'Uploaded file'}
            type={uploadedFileResource.type as ResourceType}
            resourceKey={uploadedFileResource.resourceKey}
            resourceData={uploadedFileResource.data}
          />
        ) : (
          <Text c="dimmed">No Source File</Text>
        ),
      },
      ...(sourceFileResource?.showInAppSupported
        ? [
            {
              heading: 'Content Access',
              content: <Text>{contentAccessLabel}</Text>,
            },
          ]
        : []),
      {
        heading: 'File Description',
        content: sourceFileResource?.description ? (
          <Text style={{ whiteSpace: 'pre-wrap' }}>{sourceFileResource.description}</Text>
        ) : (
          <Text c="dimmed">No description</Text>
        ),
      },
      {
        heading: 'Extracted Content',
        content: (
          <Box>
            <Paper bg={theme.lighten(theme.colors.primaryColors[1], 0.7)} radius="sm" p="md" pb="xl" shadow="0">
              <Text size="sm" fw={500} lineClamp={6} style={{ whiteSpace: 'pre-wrap' }}>
                {stepContentQuery.data?.content}
              </Text>
            </Paper>
            <Group justify="center">
              <Button
                size="sm"
                style={{ marginTop: 'calc(var(--button-height-sm) / -2)' }}
                onClick={() =>
                  data.modal.current?.openModal({
                    title: 'Previewing Extracted Content',
                    data: stepContentQuery.data?.content,
                    opts: {
                      markdown: true,
                    },
                  })
                }>
                View Full Content
              </Button>
            </Group>
          </Box>
        ),
      },
      {
        heading: 'File FAQs',
        content: <FAQSectionTable data={props.step.faqs ?? []} />,
      },
      {
        heading: 'File Supplemental Resources',
        content: <ModuleResourcesTable data={stepResourcesQuery.data ?? []} />,
      },
    ]
    return result
  }, [
    props.step.name,
    props.step.faqs,
    uploadedFileResource,
    sourceFileResource,
    contentAccessLabel,
    stepContentQuery.data,
    stepResourcesQuery.data,
    data.modal,
  ])

  return (
    <CollapsibleContentSection
      defaultCollapsed
      id={`step-${props.step.stepId}`}
      title={props.step.name ? `File Details: ${props.step.name}` : `File ${props.index + 1} Details`}
      ContentElement={<TableContentGrid headingFlexWidth="264px" rows={rows} />}
    />
  )
}
